import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'oled' | 'system';

interface ThemeState {
  theme: ThemeMode;
  highContrast: boolean;
  sidebarCollapsed: boolean;
  /** Off-canvas sidebar drawer visibility on viewports below lg. */
  mobileNavOpen: boolean;
  setTheme: (theme: ThemeMode) => void;
  toggleHighContrast: () => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileNavOpen: (open: boolean) => void;
  toggleMobileNav: () => void;
  cycleTheme: () => void;
}

const THEME_CYCLE: ThemeMode[] = ['system', 'light', 'dark', 'oled'];

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'light',
      highContrast: false,
      sidebarCollapsed: false,
      mobileNavOpen: false,
      setTheme: (theme) => set({ theme }),
      toggleHighContrast: () => set((state) => ({ highContrast: !state.highContrast })),
      toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
      setSidebarCollapsed: (collapsed) => set({ sidebarCollapsed: collapsed }),
      setMobileNavOpen: (open) => set({ mobileNavOpen: open }),
      toggleMobileNav: () => set((state) => ({ mobileNavOpen: !state.mobileNavOpen })),
      cycleTheme: () => {
        const { theme } = get();
        const currentIndex = THEME_CYCLE.indexOf(theme);
        const nextIndex = (currentIndex + 1) % THEME_CYCLE.length;
        set({ theme: THEME_CYCLE[nextIndex] });
      },
    }),
    {
      name: 'stockforge-theme',
      partialize: (state) => ({
        theme: state.theme,
        highContrast: state.highContrast,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
    }
  )
);

// Theme mode labels for UI
export const THEME_MODE_LABELS: Record<ThemeMode, string> = {
  system: 'SYSTEM',
  light: 'LIGHT',
  dark: 'DARK',
  oled: 'OLED',
};

export const THEME_MODE_DESCRIPTIONS: Record<ThemeMode, string> = {
  system: 'Follow system settings',
  light: 'Light theme with paper white background',
  dark: 'Dark theme with slate workshop background',
  oled: 'True black for OLED displays',
};
